import { ArchiveCompanyDialog } from '@/components/archive-company-dialog';
import { CompanyActionsMenu } from '@/components/company-actions-menu';
import { CompanyCard } from '@/components/company-card';
import { NewCompanyCard } from '@/components/new-company-card';
import { companiesApi, meApi } from '@/lib/api';
import { canManageCompanies } from '@/lib/company-permissions';
import { useEffectiveRole } from '@/lib/effective-role';
import { resolveLanding } from '@/lib/landing';
import type { Company, User } from '@mss_creator/shared';
import { Icon } from '@pms-digital/design-system';
import { Link, createFileRoute, redirect, useRouter } from '@tanstack/react-router';
import { useState } from 'react';

export interface IndexLoaderData {
  companies: Company[];
  me: User;
  /** How many companies are archived; always 0 for a role that may not see the archives. */
  archivedCount: number;
}

export const Route = createFileRoute('/')({
  // pms_user/pms_admin land on the company grid (fully open — every company,
  // no ownership boundary), being global roles. A user/admin with a resolved
  // membership skips straight to their company's space instead (see
  // lib/landing.ts) — the only reachable branch today is "no membership",
  // since there's no bootstrap flow yet, so it falls back to the grid too.
  loader: async (): Promise<IndexLoaderData> => {
    const me = await meApi.get();
    const membership =
      me.role === 'user' || me.role === 'admin' ? await meApi.getMembership() : null;
    const target = resolveLanding(me, membership?.companyId ?? null);
    if (target.to !== '/') {
      throw redirect({ to: target.to, params: target.params, replace: true });
    }

    // Everything the grid shows is fetched here, none of it after the first
    // render: a request fired from the page itself (the archives count, once the
    // role is known) would land on a session that expired in between and send
    // the user to /login before they did anything.
    const [companies, archived] = await Promise.all([
      companiesApi.list(),
      canManageCompanies(me.role) ? companiesApi.listArchived() : Promise.resolve([]),
    ]);
    return { companies, me, archivedCount: archived.length };
  },
  component: HomePage,
  pendingComponent: PendingScreen,
  errorComponent: ErrorScreen,
});

function HomePage() {
  const { companies, me, archivedCount } = Route.useLoaderData();
  return <CompaniesGrid companies={companies} me={me} archivedCount={archivedCount} />;
}

export interface CompaniesGridProps {
  /** The active companies, as the route's loader fetched them. */
  companies: Company[];
  me: User;
  archivedCount: number;
}

/**
 * The company grid. pms_user and pms_admin also get, on top of it, the
 * « Archives (N) » link and the « ⋯ » menu of each card (issue #20): both are
 * simply absent for the other roles, since the API refuses them anyway (403).
 *
 * Exported so the tests can render it without a router tree.
 */
export function CompaniesGrid({ companies, me, archivedCount }: CompaniesGridProps) {
  const router = useRouter();
  const [archiveTarget, setArchiveTarget] = useState<Pick<Company, 'id' | 'name'> | null>(null);

  const canManage = canManageCompanies(useEffectiveRole(me));

  return (
    <section className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-bold text-bleu-nuit text-h2">Entreprises</h1>
          <p className="text-gray-600 text-small">Sélectionnez une entreprise ou créez-en une.</p>
        </div>
        {canManage && archivedCount > 0 && (
          <Link
            to="/companies/archives"
            className="mt-2 inline-flex items-center gap-1 whitespace-nowrap rounded-sm px-2 py-1 font-semibold text-gray-700 text-small no-underline hover:bg-gray-100 hover:text-noir"
          >
            <Icon name="archive" size={16} />
            Archives ({archivedCount})
          </Link>
        )}
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[repeat(auto-fill,minmax(260px,1fr))]">
        {companies.map((company) => (
          <CompanyCard
            key={company.id}
            company={company}
            actions={
              canManage ? (
                <CompanyActionsMenu company={company} onArchive={setArchiveTarget} />
              ) : undefined
            }
          />
        ))}
        <NewCompanyCard />
      </div>

      <ArchiveCompanyDialog
        company={archiveTarget}
        onClose={() => setArchiveTarget(null)}
        // The grid comes from the route's loader, not from a query: reload it.
        onArchived={() => void router.invalidate()}
      />
    </section>
  );
}

function PendingScreen() {
  return (
    <div className="mx-auto max-w-4xl px-6">
      <p className="text-gray-500 text-small">Chargement…</p>
    </div>
  );
}

function ErrorScreen() {
  return (
    <div className="mx-auto max-w-4xl px-6">
      <section className="space-y-2">
        <h1 className="font-bold text-danger text-h3">Erreur</h1>
        <p className="text-small">Impossible de contacter le serveur. Réessayez plus tard.</p>
      </section>
    </div>
  );
}
